import { useEffect, useState } from 'react';

import { CommandLifecycle } from '@coinweb/webapp-library';
import { AlertCircleIcon, CheckIcon, CircleXIcon, Loader2Icon, QrCodeIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/shadcn/ui/card';
import { cn } from '@/utils';

import { useMessageStack } from '../hooks';
import { CoinwebWalletMessage } from '../types';

export const MessageCard = ({
  isLastMessage,
  message,
  className,
  ...props
}: {
  isLastMessage: boolean;
  message: CoinwebWalletMessage;
  className?: string;
  style: React.CSSProperties;
}) => {
  return (
    <Card className={cn(className, 'w-full')} {...props}>
      <CardContent className="h-full w-full p-4">
        <div className="flex w-full items-center justify-start gap-2 text-base font-medium tracking-wide">
          {message.type === CommandLifecycle.Initiated ? (
            <QrCodeIcon size={24} />
          ) : message.type === CommandLifecycle.Pending ? (
            isLastMessage ? (
              <Loader2Icon className="animate-spin" size={24} />
            ) : (
              <CheckIcon size={24} />
            )
          ) : message.type === CommandLifecycle.Confirmed ? (
            <CheckIcon size={24} />
          ) : message.type === CommandLifecycle.Cancelled ? (
            <CircleXIcon size={24} />
          ) : message.type === CommandLifecycle.Failed ? (
            <AlertCircleIcon size={24} />
          ) : null}
          <span className="flex-1">{message.message}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export const CoinwebQrMessageStack = ({
  message,
  className,
}: {
  message: CoinwebWalletMessage | null;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const messages = useMessageStack(message);

  const maxStack = 3;
  const stackedMessages = messages.slice(-maxStack);
  const lastIndex = stackedMessages.length - 1;
  const hasMessages = stackedMessages.length > 0;

  useEffect(() => {
    if (hasMessages) {
      const id = requestAnimationFrame(() => setIsOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setIsOpen(false);
  }, [hasMessages]);

  if (!hasMessages) {
    return null;
  }

  const expandedHeight = stackedMessages.length > 1 ? `calc(${stackedMessages.length} * 3rem)` : '3.5rem';
  const containerHeight = !isOpen ? '0' : expandedHeight;

  return (
    <div
      className={cn(
        `text-muted-foreground relative flex w-full max-w-sm items-start justify-center overflow-visible text-base
        transition-[height] duration-500 ease-in-out`,
        className
      )}
      style={{ height: containerHeight }}
    >
      {stackedMessages.map((m: CoinwebWalletMessage, idx: number) => {
        const scale = 1 - (lastIndex - idx) * 0.04;
        const opacity = 1 - (lastIndex - idx) * 0.25;
        const step = 75;
        const translateY = step * (lastIndex - idx);

        return (
          <div
            key={m.id}
            className={`animate-in fade-in slide-in-from-top-[100%] pointer-events-none absolute h-14 w-full
            duration-300 ease-out`}
            style={{ zIndex: idx }}
          >
            <MessageCard
              isLastMessage={idx === lastIndex}
              message={m}
              style={{
                transform: `translateY(${translateY}%) scale(${scale})`,
                opacity: opacity,
              }}
              className={cn('w-full origin-top shadow-sm transition-all duration-500', {
                ['bg-emerald-500']: m.type === CommandLifecycle.Confirmed,
                ['bg-rose-500']: m.type === CommandLifecycle.Cancelled || m.type === CommandLifecycle.Failed,
              })}
            />
          </div>
        );
      })}
    </div>
  );
};
